"use client";

import { Suspense, useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { HubspotMeetingEmbed } from "@/components/marketing/HubspotMeetingEmbed";
import {
  HELLO_EMAIL,
  PARTNERS_EMAIL,
  SUPPORT_EMAIL,
} from "@/lib/config/brand";
import { APP_URL } from "@/lib/env";

const TOPIC_LABELS: Record<string, string> = {
  sales: "Sales & Pricing",
  partnership: "Partnerships",
  "revops-demo": "RevOps Demo Guide",
  "platform-guardrails": "Platform Guardrails",
  "partner-playbook": "Partner Playbook",
};

// Custom tilt hook for 3D card effect
function useTilt() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientY - rect.top) / rect.height - 0.5;
    const y = -((e.clientX - rect.left) / rect.width - 0.5);
    setTilt({ x: x * 10, y: y * 10 });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  return { ref, tilt, handleMouseMove, handleMouseLeave };
}

// Tilt card wrapper component
interface TiltCardProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

function TiltCard({ children, delay = 0, className = "" }: TiltCardProps) {
  const { ref, tilt, handleMouseMove, handleMouseLeave } = useTilt();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transformStyle: "preserve-3d",
      }}
      className={`glass-panel-hover transition-transform duration-200 ${className}`}
    >
      {children}
    </motion.div>
  );
}

export default function ContactPage() {
  const [topic, setTopic] = useState("sales");
  const topicLabel = TOPIC_LABELS[topic] ?? "General Inquiry";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- query param read on mount
    setTopic(params.get("topic") ?? "sales");
  }, []);

  return (
    <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col gap-10 px-6 pb-20 pt-24">
      {/* Background aurora orbs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -left-40 top-40 h-[500px] w-[500px] rounded-full bg-brand-primary/10 blur-[150px]" />
        <div className="absolute -right-40 top-1/3 h-[600px] w-[600px] rounded-full bg-brand-secondary/10 blur-[180px]" />
        <div className="absolute left-1/3 bottom-20 h-[400px] w-[400px] rounded-full bg-brand-secondary/5 blur-[120px]" />
      </div>

      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-4 text-center"
      >
        <Badge className="border-brand-primary/40 bg-brand-primary/10 text-brand-primary">
          Contact Wrenk
        </Badge>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
          Tell us about the{" "}
          <span className="text-primary font-semibold">mission</span>{" "}
          you&apos;re running.
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-slate-700">
          Share your goals and we&apos;ll line up the right architect to help. We typically respond within one business day.
        </p>
      </motion.header>

      <div className="grid gap-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="glass-panel-hover p-6"
        >
          <h2 className="mb-1 text-xl font-semibold text-slate-900">Book time with our team</h2>
          <p className="mb-5 text-sm text-muted-foreground">
            Choose a slot that works for you and we&apos;ll prepare a walkthrough focused on {topicLabel.toLowerCase()}.
          </p>
          <Suspense fallback={<div className="min-h-[600px] rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-muted-foreground text-sm">Loading scheduler...</div>}>
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
              <HubspotMeetingEmbed />
            </div>
          </Suspense>
        </motion.div>

        <div className="space-y-6">
          <TiltCard delay={0.2} className="p-6">
            <h3 className="mb-1 text-lg font-semibold text-slate-900">Prefer another channel?</h3>
            <p className="mb-5 text-sm text-muted-foreground">
              Reach us directly — whatever helps you move faster.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 rounded-lg border border-white/8 bg-slate-50 px-4 py-3">
                <span className="text-muted-foreground">Email</span>
                <Link
                  href={`mailto:${HELLO_EMAIL}`}
                  className="ml-auto font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                >
                  {HELLO_EMAIL}
                </Link>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-white/8 bg-slate-50 px-4 py-3">
                <span className="text-muted-foreground">Support</span>
                <Link
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="ml-auto font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                >
                  {SUPPORT_EMAIL}
                </Link>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-white/8 bg-slate-50 px-4 py-3">
                <span className="text-muted-foreground">Partners</span>
                <Link
                  href={`mailto:${PARTNERS_EMAIL}`}
                  className="ml-auto font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                >
                  {PARTNERS_EMAIL}
                </Link>
              </div>
            </div>
          </TiltCard>

          <TiltCard delay={0.3} className="p-6">
            <h3 className="mb-1 text-lg font-semibold text-slate-900">Already a customer?</h3>
            <p className="mb-5 text-sm text-muted-foreground">
              Log in to access your AI Consultant Team for health checks, guardrails, and deployment missions.
            </p>
            <Link
              href={APP_URL}
              className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-bold text-neutral-900 transition-all hover:bg-primary/90 shadow-[0_0_20px_rgba(37,99,235,0.2)] hover:shadow-[0_0_30px_rgba(37,99,235,0.35)]"
            >
              Go to AI Consultant Team
            </Link>
          </TiltCard>
        </div>
      </div>
    </div>
  );
}

